'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement, useCallback, useEffect, useRef, useState, } from 'react';
import { useReducedMotion } from './internal/env.js';
import { cssVars, cx } from './internal/style.js';
/** Stagger never delays past the sixth sibling; see `.bk-stagger` in tokens.css. */
export const STAGGER_CAP = 6;
/** The class for an entrance variant, for elements a component cannot wrap. */
export function enterClass(variant = 'rise', expressive = false) {
    return cx(`bk-enter-${variant}`, expressive && 'bk-motion-expressive');
}
/**
 * The inline style for the `index`-th sibling of a staggered entrance. For a
 * list that is not a direct parent/child pair, where `.bk-stagger` cannot
 * reach.
 */
export function staggerStyle(index, base) {
    const step = Math.max(0, Math.min(Math.floor(index), STAGGER_CAP));
    return cssVars({ '--bk-enter-delay': `calc(${step} * var(--bk-stagger))` }, base);
}
/** A one-shot entrance on mount. */
export function Enter({ as = 'div', variant = 'rise', expressive = false, index, className, style, children, ...rest }) {
    return createElement(as, {
        ...rest,
        className: cx(enterClass(variant, expressive), className),
        style: index === undefined ? style : staggerStyle(index, style),
    }, children);
}
/**
 * Its direct children rise in one after another. A child that carries an
 * `.bk-enter-*` class (or is an `<Enter>`) keeps its own variant and the
 * stagger delay.
 */
export function Stagger({ as = 'div', expressive = false, className, children, ...rest }) {
    return createElement(as, { ...rest, className: cx('bk-stagger', expressive && 'bk-motion-expressive', className) }, children);
}
/**
 * Longest an exit may hold the element before it is removed anyway: the exit
 * runs on `--bk-duration-fast`, and a missed `animationend` (a display:none
 * ancestor, a background tab) must not leave a dead element on screen.
 */
const EXIT_FALLBACK_MS = 400;
/**
 * Mount/unmount with an exit animation — the one thing CSS cannot do alone.
 *
 * Under reduced motion the exit is skipped outright; the entrance still gets
 * the stylesheet's short crossfade.
 *
 * Inside AntD's `<Space>`, a hidden `Presence` renders nothing, and the empty
 * `.ant-space-item` it leaves is hidden by AntD's own `:empty` rule, so no gap
 * is left behind. That holds only while the item is truly empty: an
 * always-mounted sibling in the same item (a live region, a hidden label)
 * keeps it visible and the gap comes back. Give such a sibling its own
 * wrapper outside the `Space`.
 */
export function Presence({ show, enter = 'fade', exit = 'fade', appear = true, expressive = false, onExited, as = 'div', className, children, onAnimationEnd, ...rest }) {
    const reduced = useReducedMotion();
    const [mounted, setMounted] = useState(show);
    const [exiting, setExiting] = useState(false);
    const mountedRef = useRef(show);
    const animateEnter = useRef(appear);
    const previousShow = useRef(show);
    const onExitedRef = useRef(onExited);
    onExitedRef.current = onExited;
    const finish = useCallback(() => {
        if (!mountedRef.current)
            return;
        mountedRef.current = false;
        setMounted(false);
        setExiting(false);
        onExitedRef.current?.();
    }, []);
    useEffect(() => {
        // Any appearance after the first render is a change the user caused, so
        // it animates whatever `appear` said. Compared against the previous value
        // rather than a first-run flag, so StrictMode's double effect is a no-op.
        if (show && !previousShow.current)
            animateEnter.current = true;
        previousShow.current = show;
        if (show) {
            mountedRef.current = true;
            setMounted(true);
            setExiting(false);
            return undefined;
        }
        if (!mountedRef.current)
            return undefined;
        if (reduced || exit === 'none') {
            finish();
            return undefined;
        }
        setExiting(true);
        const timer = window.setTimeout(finish, EXIT_FALLBACK_MS);
        return () => window.clearTimeout(timer);
    }, [show, reduced, exit, finish]);
    if (!mounted)
        return null;
    const stateClass = exiting
        ? exit === 'none'
            ? undefined
            : `bk-exit-${exit}`
        : animateEnter.current
            ? enterClass(enter, expressive)
            : undefined;
    return createElement(as, {
        ...rest,
        className: cx(stateClass, className),
        'data-state': exiting ? 'exiting' : 'entered',
        onAnimationEnd: (event) => {
            onAnimationEnd?.(event);
            if (exiting && event.target === event.currentTarget)
                finish();
        },
    }, children);
}
const length = (value) => typeof value === 'number' ? `${value}px` : value;
/**
 * One placeholder shape, in the skeleton tokens, with the highlight sweeping
 * in reading order. Decorative: wrap a group of them in `<SkeletonGroup>`,
 * which carries the accessible name.
 *
 * Framework-free on purpose. The panels also have AntD's `Skeleton`, which is
 * themed from the same colours and is the right choice for a generic
 * paragraph; this one is for a placeholder shaped like a specific layout.
 */
export function SkeletonBlock({ shape = 'block', width, height, className, style, ...rest }) {
    const block = length(height);
    const inline = shape === 'circle' ? (length(width) ?? block) : length(width);
    return (_jsx("span", { ...rest, "aria-hidden": "true", className: cx('bk-skeleton', className), "data-shape": shape, style: {
            inlineSize: inline,
            blockSize: shape === 'circle' ? (block ?? inline) : block,
            ...style,
        } }));
}
/** A paragraph-shaped stack of text lines. */
export function SkeletonLines({ lines = 3, lastLineWidth = '60%', className, style, ...rest }) {
    return (_jsx("span", { ...rest, "aria-hidden": "true", className: className, style: { display: 'flex', flexDirection: 'column', gap: 'var(--bk-space-2)', ...style }, children: Array.from({ length: Math.max(1, lines) }, (_, i) => (_jsx(SkeletonBlock, { shape: "text", width: i === lines - 1 && lines > 1 ? lastLineWidth : '100%' }, i))) }));
}
/**
 * The accessible wrapper for a skeleton layout: one polite status with a
 * Persian label, and the shapes inside hidden from assistive technology.
 */
export function SkeletonGroup({ label = 'در حال بارگذاری…', children, ...rest }) {
    return (_jsxs("div", { role: "status", "aria-busy": "true", "aria-live": "polite", ...rest, children: [_jsx("span", { className: "bk-visually-hidden", children: label }), children] }));
}
//# sourceMappingURL=presence.js.map